// Strip the quoted-original-message tail that email clients append to a
// reply (Apple Mail / Gmail's "On <date>, at <time>, <name> <email> wrote:",
// Outlook's "-----Original Message-----" or a bare "From:/Sent:/Subject:"
// header block, and runs of ">"-prefixed lines) so the correspondence view
// shows what the sender actually said, not everything they replied on top
// of. Display-only: the stored `body` is never touched, so nothing here
// affects classification, export, or the evidentiary record.

const QUOTE_START_PATTERNS = [
  // Apple Mail / Gmail, with or without a leading ">" from a quoted reply
  // replying to a reply: "On 21 Sep 2026, at 16:58, Kieran Jones <x@y.com> wrote:"
  /^>*\s*On\s.{0,120}\swrote:\s*$/i,
  // Outlook
  /^>*\s*-{2,}\s*Original Message\s*-{2,}\s*$/i,
];

const isQuoteMarkerLine = (line) => /^\s*>/.test(line);
// A "From: …" line only counts as the start of a quoted header block when a
// "Sent:"/"Date:" line follows within the next couple of lines — on its own
// it's too easy to false-positive on a body that just happens to start a
// sentence with "From ...".
const isFromHeaderLine = (line) => /^>*\s*From:\s+\S/i.test(line);
const isSentOrDateHeaderLine = (line) => /^>*\s*(Sent|Date):\s+\S/i.test(line);

/**
 * Returns `body` with any trailing quoted-original-message block removed.
 * Falls back to the original body if stripping would remove everything (a
 * misfire is better than showing nothing) or nothing quote-like is found.
 */
export function stripQuotedReply(body) {
  if (!body) return body;
  const lines = body.replace(/\r\n/g, '\n').split('\n');

  let cutAt = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (QUOTE_START_PATTERNS.some((re) => re.test(line))) {
      cutAt = i;
      break;
    }
    // Two consecutive quote-marker lines: the start of a quoted block with
    // no recognisable header (e.g. the reply client didn't add one).
    if (isQuoteMarkerLine(line) && isQuoteMarkerLine(lines[i + 1] || '')) {
      cutAt = i;
      break;
    }
    if (isFromHeaderLine(line) && [lines[i + 1], lines[i + 2]].some((l) => l && isSentOrDateHeaderLine(l))) {
      cutAt = i;
      break;
    }
  }

  if (cutAt === -1) return body;

  const kept = lines.slice(0, cutAt).join('\n').replace(/\s+$/, '');
  return kept || body;
}
